// Curated, trauma-informed affirmation card deck.
// 20 categories, each crafted to be gentle, non-prescriptive, non-clinical.
// Structured so new categories/cards can be added without UI changes.

export type AffirmationCardCategoryId =
  | "self_worth"
  | "self_compassion"
  | "safety"
  | "anxiety"
  | "ptsd"
  | "cptsd"
  | "inner_child"
  | "relationships"
  | "boundaries"
  | "grief"
  | "hope"
  | "strength"
  | "nervous_system"
  | "sleep"
  | "trust"
  | "forgiveness"
  | "daily_motivation"
  | "emotional_regulation"
  | "self_acceptance"
  | "growth"
  | "recovery";

export type AffirmationCardCategory = {
  id: AffirmationCardCategoryId;
  label: string;
  emoji: string;
  description: string;
};

export const AFFIRMATION_CARD_CATEGORIES: AffirmationCardCategory[] = [
  { id: "self_worth", label: "Self-Worth", emoji: "💛", description: "You are enough, exactly as you are." },
  { id: "self_compassion", label: "Self-Compassion", emoji: "🌿", description: "Speak to yourself with kindness." },
  { id: "safety", label: "Safety", emoji: "🛡", description: "Building safety inside your own body." },
  { id: "anxiety", label: "Anxiety", emoji: "🌊", description: "Soft landings for hard moments." },
  { id: "ptsd", label: "PTSD", emoji: "🧠", description: "Gentle reminders when the past feels loud." },
  { id: "cptsd", label: "CPTSD", emoji: "🌱", description: "For the long, layered work." },
  { id: "inner_child", label: "Inner Child", emoji: "👶", description: "Meeting the younger you with love." },
  { id: "relationships", label: "Relationships", emoji: "❤️", description: "Safe connection, healthy love." },
  { id: "boundaries", label: "Boundaries", emoji: "🚪", description: "Protecting your peace is allowed." },
  { id: "grief", label: "Grief", emoji: "🌸", description: "Love that has nowhere to land." },
  { id: "hope", label: "Hope & Encouragement", emoji: "🌈", description: "Light for the path ahead." },
  { id: "strength", label: "Strength & Resilience", emoji: "💪", description: "Quiet strength, already yours." },
  { id: "nervous_system", label: "Nervous System", emoji: "🦋", description: "Coming back to your body." },
  { id: "sleep", label: "Sleep & Nightmares", emoji: "🌙", description: "Soft words for the night." },
  { id: "trust", label: "Trust", emoji: "🤍", description: "Learning to trust again, slowly." },
  { id: "forgiveness", label: "Forgiveness", emoji: "🌻", description: "Releasing what you carry." },
  { id: "daily_motivation", label: "Daily Motivation", emoji: "🌞", description: "A gentle push, not a shove." },
  { id: "emotional_regulation", label: "Emotional Regulation", emoji: "🌺", description: "Riding the waves with care." },
  { id: "self_acceptance", label: "Self-Acceptance", emoji: "🌼", description: "You, whole and welcome." },
  { id: "growth", label: "Growth & Recovery", emoji: "✨", description: "Becoming, at your own pace." },
];

export const AFFIRMATION_CARDS: Record<AffirmationCardCategoryId, string[]> = {
  self_worth: [
    "You are worthy of love, even on the days you can't feel it.",
    "Your worth was never something you had to earn.",
    "You are allowed to take up space, softly.",
    "Being is enough today.",
    "You belong to yourself first.",
    "You don't have to perform to deserve kindness.",
    "Your value is not measured by your productivity.",
    "You are loved exactly as you are right now.",
    "There is nothing wrong with you. You are healing.",
    "You matter, simply because you exist.",
    "You are not too much. You never were.",
    "Your worth doesn't shrink when you're tired.",
    "You are allowed to receive without earning it.",
    "You are more than the roles you play for others.",
  ],
  self_compassion: [
    "I can speak to myself the way I would speak to someone I love.",
    "I am allowed to be a beginner at this.",
    "It's okay that this is hard. I am being kind to myself anyway.",
    "I can hold myself gently through this moment.",
    "I forgive myself for not knowing what I didn't know.",
    "My feelings make sense. I don't have to fix them right now.",
    "I am doing the best I can with what I have today.",
    "I am allowed to rest without earning it.",
    "I can be patient with the parts of me that are still healing.",
    "My softness is not a weakness. It is a way home.",
    "I can meet my mistakes with curiosity instead of shame.",
    "I don't have to be perfect to be worthy of care.",
    "I can let myself be human today.",
    "Kindness towards myself is not weakness — it is repair.",
  ],
  safety: [
    "I am safe in this moment.",
    "My body is allowed to soften a little now.",
    "This room, this breath, this floor — all here to hold me.",
    "I can build safety in small, daily ways.",
    "I am not in the past. I am here, now.",
    "I am allowed to feel safe, even when it is unfamiliar.",
    "I can trust the safety I have created.",
    "Safety is a place I am learning to return to.",
    "I can leave anything that does not feel safe.",
    "My nervous system is learning that it is okay to rest.",
    "I do not have to be on guard all the time.",
    "I can let this moment be quiet.",
    "I am safer than my body sometimes remembers.",
  ],
  anxiety: [
    "This is a wave. It will pass through me.",
    "I am safe in this moment. My body just needs a minute to catch up.",
    "I can breathe slowly. Nothing has to be solved right now.",
    "My anxiety is not a fact. It is a feeling.",
    "Soft breath in. Soft breath out. I am here.",
    "I can place my feet on the ground and let the floor hold me.",
    "I don't have to believe every thought I think.",
    "I have made it through every panicked moment before this one.",
    "I can ride this without fighting it.",
    "There is room for me to feel this and still be okay.",
    "My anxiety is trying to protect me. I can thank it and choose again.",
    "I can be anxious and still be safe.",
    "Slowing down is allowed, even when my mind is fast.",
  ],
  ptsd: [
    "The memory is loud, but I am here in the present.",
    "My body is remembering. I am not in danger now.",
    "Flashbacks are not the truth of this moment.",
    "I can name five things I see and come back to now.",
    "What I lived through was real. What I am living now is also real.",
    "Triggers are information, not instructions.",
    "I can be gentle with myself when the past comes close.",
    "My reactions make sense given what I survived.",
    "I am allowed to take my time returning to the present.",
    "Healing does not mean forgetting. It means feeling safer to remember.",
    "I can hold what happened without it holding me.",
    "The past is not happening again, even when it feels like it is.",
    "I survived it then. I do not have to survive it again today.",
  ],
  cptsd: [
    "What happened to me was not my fault, and it was not small.",
    "My patterns make sense. I learned them to stay safe.",
    "I am allowed to grieve what I did not get.",
    "I can build the safety I never had, piece by piece.",
    "There is nothing wrong with me for taking this long.",
    "My inner critic learned its voice from somewhere. I can teach it a new one.",
    "I do not have to earn belonging by disappearing.",
    "I am allowed to have needs. Having them does not make me too much.",
    "The child in me deserved so much more. I can offer some of it now.",
    "Healing complex trauma is long, quiet work. I am doing it.",
    "I can be a work in progress and still be worthy of love.",
    "My nervous system is not broken. It adapted.",
    "I am allowed to rewrite the story I was told about myself.",
  ],
  inner_child: [
    "Little one, you are safe with me now.",
    "It was never your fault. You were doing your best.",
    "I see you. I believe you. I am not leaving.",
    "You are allowed to play, to rest, to be small.",
    "You do not have to be good to be loved.",
    "Your feelings mattered then, and they matter now.",
    "I am the adult you needed. I am here.",
    "You are allowed to want what you wanted.",
    "You are not too sensitive. You were paying attention.",
    "You can put the weight down. I will carry it with you now.",
    "You deserved gentleness. I can give it to you today.",
    "You are loved for who you are, not what you did.",
    "It is safe to be a child inside a grown-up body.",
  ],
  relationships: [
    "Safe love is quiet, consistent, and kind.",
    "I deserve people who choose me clearly.",
    "I do not have to shrink to be loved.",
    "My needs in a relationship are valid.",
    "I can let go of what was never truly mine.",
    "I am allowed to be loved without earning it.",
    "Healthy love feels like rest, not work.",
    "I can be soft and still have strong boundaries.",
    "Real intimacy includes my whole self, not just the easy parts.",
    "I am building the relationships my younger self needed.",
    "I am allowed to move slowly with new people.",
    "I do not owe access to anyone who has hurt me.",
    "Love that requires me to abandon myself is not love I need.",
    "I can be lonely and still be worthy of good company.",
  ],
  boundaries: [
    "I can say no without explaining myself.",
    "Protecting my peace is not selfish.",
    "I am allowed to disappoint people to honour myself.",
    "My yes means more when I am also free to say no.",
    "I get to choose who has access to me.",
    "Distance can be an act of love — for them and for me.",
    "I do not have to over-explain to be understood.",
    "I am allowed to change my mind.",
    "My limits are valid even when others don't like them.",
    "I can close a door without anger.",
    "'No' is a full sentence.",
    "Guilt is not proof that my boundary is wrong.",
    "I can hold a limit and still hold love.",
  ],
  grief: [
    "Grief is love with nowhere to land. I am allowed to feel it all.",
    "I can miss what I lost and still keep living.",
    "There is no timeline for this. I can take as long as I need.",
    "It's okay to cry. It's also okay to laugh today.",
    "Carrying this does not mean I am broken.",
    "I am allowed to remember without falling apart.",
    "The love does not leave when the person does.",
    "I can hold space for what is gone and what is still here.",
    "My grief is proof of how deeply I loved.",
    "Some days will be soft. Some will be heavy. Both are part of the way through.",
    "I do not have to be done with this.",
    "Grief can live alongside joy.",
    "I am not moving on. I am moving with.",
  ],
  hope: [
    "There is more for me than this moment.",
    "The path ahead is allowed to surprise me.",
    "I have already survived 100% of my hardest days.",
    "Something gentle is on its way.",
    "I am becoming someone I will be proud of.",
    "Today does not have to be the whole story.",
    "Small light is still light.",
    "I am allowed to imagine a softer future.",
    "Hope does not have to be loud to be real.",
    "I am still here. That is enough for today.",
    "The version of me that survives this is already on their way.",
    "I can hold hope quietly, like a candle.",
    "It is okay to believe things can get better.",
  ],
  strength: [
    "I have carried impossible things and I am still here.",
    "My strength does not have to look loud to be real.",
    "Softness is a kind of strength too.",
    "I do not have to feel strong to be strong.",
    "Every small step I take is proof of my resilience.",
    "I can bend without breaking.",
    "I have survived difficult moments before, and I can face today.",
    "Rest is part of my strength, not the opposite of it.",
    "I am stronger than the story that says I am not.",
    "My resilience does not require me to be okay all the time.",
    "I can be tired and strong in the same breath.",
    "I am allowed to be proud of how far I've come.",
    "The strength that got me here is still with me.",
  ],
  nervous_system: [
    "My body is allowed to slow down now.",
    "I can offer my nervous system one long exhale.",
    "It is safe to soften my shoulders.",
    "I can unclench my jaw and let this moment be.",
    "My body is not the enemy. It is the messenger.",
    "I can move gently to help my body come back.",
    "I am allowed to co-regulate — with a person, a pet, a place.",
    "I can ground through my feet, my breath, my hands.",
    "Freeze, fight, flight, fawn — all of these were once wise.",
    "I can meet my body's activation with curiosity.",
    "My window of tolerance can widen with practice.",
    "I do not have to fix my nervous system. I can befriend it.",
    "Slower breath, softer body, safer moment.",
  ],
  sleep: [
    "It is safe to rest tonight.",
    "My body has permission to soften into sleep.",
    "I can let go of today. Tomorrow will still be there.",
    "Rest is productive.",
    "I do not have to solve anything before I sleep.",
    "If I wake, I can breathe and begin again.",
    "Nightmares are not predictions. They are old alarms.",
    "I am allowed to comfort myself in the dark.",
    "This night will pass. Morning is on its way.",
    "My bed is a place I am allowed to feel held.",
    "I can offer my body one kind breath and let it rest.",
    "Even broken sleep still counts as rest.",
    "I am safe enough to close my eyes.",
  ],
  trust: [
    "I am learning to trust myself again.",
    "I can trust my no. I can trust my yes.",
    "My intuition is worth listening to.",
    "Trust can be rebuilt in small, honest moments.",
    "I do not have to trust everyone. Discernment is love too.",
    "I can trust the pace of my own healing.",
    "It is safe to trust the people who show up consistently.",
    "My gut has been keeping me alive. I can listen to it.",
    "I can trust the version of me who has made it this far.",
    "Trust is not all-or-nothing. I can offer a little at a time.",
    "I can trust that rest will not undo me.",
    "I can trust that I am allowed to change my mind.",
    "I am learning that safe people exist.",
  ],
  forgiveness: [
    "I can forgive myself for surviving the only way I knew how.",
    "Forgiveness does not mean what happened was okay.",
    "I do not owe anyone my forgiveness on their timeline.",
    "I can release what I carry, at my own pace.",
    "I forgive the parts of me that were only trying to protect me.",
    "I can set down what was never mine to hold.",
    "Forgiving myself is not the same as excusing myself.",
    "I am allowed to grieve and to release, in the same breath.",
    "I can loosen my grip on what I cannot change.",
    "I do not have to forgive to be free.",
    "I can offer myself the softness I was never given.",
    "I forgive myself for the person I had to be to survive.",
    "Letting go can be quiet. It does not have to be loud.",
  ],
  daily_motivation: [
    "One small step is still a step.",
    "Today, I only have to do the next kind thing.",
    "Showing up counts, even when it's messy.",
    "I do not have to feel motivated to begin.",
    "Getting out of bed today is already something.",
    "I can do hard things slowly.",
    "Progress is allowed to be quiet.",
    "I can lower the bar today and still be proud of myself.",
    "The smallest action still moves me forward.",
    "I can begin again as many times as I need.",
    "I am allowed to celebrate what other people wouldn't notice.",
    "Today's win might just be softness. That counts.",
    "I do not have to earn tomorrow. I can just walk towards it.",
  ],
  emotional_regulation: [
    "I can feel this without becoming it.",
    "My feelings are visitors, not verdicts.",
    "I can name what I feel and let it move through me.",
    "I do not have to act on every emotion I notice.",
    "It is okay to pause before I respond.",
    "I can be angry and still be safe to be around.",
    "I can be sad without collapsing into it.",
    "I am allowed to feel two things at once.",
    "Big feelings do not make me a bad person.",
    "I can meet myself in the middle of a wave.",
    "I can breathe through this instead of numbing it.",
    "My feelings are data, not danger.",
    "I can let this feeling be here without letting it take the wheel.",
  ],
  self_acceptance: [
    "I am allowed to be exactly who I am today.",
    "I do not have to be finished to be worthy.",
    "I can accept the parts of me that are still healing.",
    "There is nothing about me that needs to be hidden to be loved.",
    "I can meet my body where it is.",
    "I am allowed to be a work in progress.",
    "I do not owe anyone a smaller, tidier version of me.",
    "My contradictions are allowed to exist.",
    "I can accept myself before I fully understand myself.",
    "I am allowed to like myself today.",
    "I am not a project. I am a person.",
    "I can stop apologising for existing.",
    "I am becoming, and I am also already enough.",
  ],
  growth: [
    "Healing takes time, and I honour my own pace.",
    "I am growing in ways I cannot always see.",
    "Recovery is not a straight line. It is a spiral.",
    "I can outgrow beliefs that used to keep me safe.",
    "I am allowed to become someone new.",
    "Small changes are still changes.",
    "I am no longer the person who could not survive this.",
    "Every gentle choice I make is part of my recovery.",
    "I am allowed to be proud of quiet progress.",
    "My growth does not have to be beautiful to be real.",
    "I can honour who I was, and still choose who I am becoming.",
    "There is no finish line. There is only this next kind step.",
    "I am building a life that feels like mine.",
  ],
  recovery: [
    "Healing is not linear, and I am still making progress.",
    "Coming back to myself is the work, and I am doing it.",
    "Small steps still count as steps.",
    "What happened was not my fault.",
    "I am rebuilding safety inside my own body.",
    "I do not have to do everything today.",
    "I am allowed to feel safe, in my own time.",
    "Recovery is allowed to take as long as it takes.",
    "I survived. Now I am learning to live.",
    "My past does not define what comes next.",
    "Every gentle day is a brick in the life I am building.",
    "I am allowed to want more than survival.",
  ],
};

export function getCardsForCategories(
  ids: AffirmationCardCategoryId[],
): { text: string; category: AffirmationCardCategoryId }[] {
  const pool = ids.length ? ids : AFFIRMATION_CARD_CATEGORIES.map((c) => c.id);
  const out: { text: string; category: AffirmationCardCategoryId }[] = [];
  for (const c of pool) {
    const cards = AFFIRMATION_CARDS[c];
    if (!cards) continue;
    for (const t of cards) out.push({ text: t, category: c });
  }
  return out;
}

export function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Deterministic hash for a card's text, used for "recent" tracking & daily pick.
export function hashCardText(text: string): string {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (h * 31 + text.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

// Build a deck that:
//  - starts with the "card of the day" (deterministic per date + filter),
//  - de-prioritises the most recently viewed cards,
//  - shuffles the rest.
export function buildDailyDeck(
  cards: { text: string; category: AffirmationCardCategoryId }[],
  opts: { dateISO: string; recentIds: string[] },
): { text: string; category: AffirmationCardCategoryId }[] {
  if (!cards.length) return [];
  const recent = new Set(opts.recentIds);

  // Deterministic index from date so "today's card" is stable per day per pool.
  let seed = 0;
  const key = `${opts.dateISO}|${cards.length}`;
  for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) >>> 0;
  const dailyIndex = seed % cards.length;
  const daily = cards[dailyIndex];

  const rest = cards.filter((_, i) => i !== dailyIndex);
  const fresh = shuffle(rest.filter((c) => !recent.has(hashCardText(c.text))));
  const seen = shuffle(rest.filter((c) => recent.has(hashCardText(c.text))));
  return [daily, ...fresh, ...seen];
}
